import { TextureData } from "../../types/types";
import { Padding } from "../../utils/Padding";
import { TextBubble } from "./TextBubble";


export class TextIconBubble 
    extends TextBubble
{
    public icon: Phaser.GameObjects.Image;
    protected iconTextureData: TextureData;

    protected itemSpacing: Padding = new Padding(10);
    protected iconScale: number;

    protected facing: Phaser.Geom.Point;
    protected contentGrowthX: number;

    private CONTENTGROWTHX_OFFSET: number = 40;
    

    public override Init(
        bgTextureData: TextureData,
        bgSideWidth: number,
        tipTextureData: TextureData,
        bgEffectsPadding?: Padding,
        fontSize?: number,
        fontFamily?: string,
        iconScale?: number,
    ): void
    {
        // > padding
        // >> contentSize
        // >> totalSize
        // >> contentSize
        // >> origin
        super.Init(bgTextureData, bgSideWidth, tipTextureData, bgEffectsPadding, fontSize, fontFamily);
        
        this.iconScale = iconScale;
    }

    public Build(): void
    {
        super.Build();

        this.icon = this.scene.make.image({});
        this.icon.setScale(this.iconScale);
        
        this.add(this.icon);
    }

    /**
     * @param iconTextureData if null, do not use imgIcon during 'Say' (declaration)
     * @param updateView if true, update position/size of imgIcon/label
     */
    public SetIconTextureData(iconTextureData: TextureData, updateView: boolean = false): void
    {
        this.iconTextureData = iconTextureData;

        if (iconTextureData) {
            this.icon.setTexture(iconTextureData.texture, iconTextureData.frame);
            this.icon.visible = true;
        }
        else {
            this.icon.visible = false;
        }

        if (updateView) {
            this.UpdateView();
        }
    }

    /**
     * Alternative to @see SetOrigin that correctly origins and grows bubble content for different orientations.
     * @param facing 
     * @param contentGrowthX 
     */
    public SetFacingAndGrowthDirection(facing: Phaser.Geom.Point, contentGrowthX: number, updateView: boolean = false): void
    {
        this.facing = facing;
        this.contentGrowthX = contentGrowthX;

        if (updateView) {
            this.UpdateView();
        }
    }
    
    public override UpdateView(): void
    {
        // reimplement updateview from scratch
        // super.UpdateView();

        // calculate size from children size and spacing+padding
        const iconWidth = this.iconTextureData ? this.icon.displayWidth : 0;
        const iconHeight = this.iconTextureData ? this.icon.displayHeight : 0;
        let itemSpacingHorizontal = (this.iconTextureData != undefined && this.txtLabel.text != "") ?
            this.itemSpacing.horizontal : 0;

        // calculate content size
        this.contentSize.Set(
            this.padding.left + this.txtLabel.displayWidth + this.padding.right + iconWidth + itemSpacingHorizontal,
            this.padding.top + Math.max(this.txtLabel.displayHeight, iconHeight) + this.padding.bottom,
        );

        // calculate total size (content size plus embedded bg effects)
        this.totalSize.Set(
            this.bgEffectsPadding.left + this.contentSize.width + this.bgEffectsPadding.right,
            this.bgEffectsPadding.top + this.contentSize.height + this.bgEffectsPadding.bottom,
        );

        // set bg size
        this.bg.setSize(this.totalSize.width, this.totalSize.height);

        /*
        Special case, where bubble grows vertically, but content is NOT center-aligned, only partially.
        Example: East:
            speechBubble: {
                portrait: {
                    facing: new Phaser.Geom.Point(0, -1),
                    contentGrowthX: -1,
                },
        */
        // Use facing and contentGrowthX to position/align content around container's (0,0) 
        // NOTE: bg is a nineslice
        // NOTE: bg origin is always middle (0.5, 0.5)
        // NOTE: bgTip visual: ">" (right facing tip); bgTip.originX/Y always (0.5, 0.5)
        // from right to left
        // this.bg.visible = false;
        // FACING X
        if (this.facing.x === -1) {
            this.bgTip.angle = 0;
            this.bgTip.setPosition(-this.bgTip.displayWidth * 0.5, 0);
            this.bg.setPosition(-this.bgTip.displayWidth - this.contentSize.width * 0.5, 0);
        }
        else if (this.facing.x === 1) {
            this.bgTip.angle = 180;
            this.bgTip.setPosition(this.bgTip.displayWidth * 0.5, 0);
            this.bg.setPosition(this.bgTip.displayWidth + this.contentSize.width * 0.5, 0);
        }
        
        // FACING Y
        if (this.facing.y === -1) {
            this.bgTip.angle = 90;
            this.bgTip.setPosition(0, -this.bgTip.displayHeight * 0.5);
            if (this.contentGrowthX == -1) {
                const halfContentWidth = Math.max(this.contentSize.width * 0.5, 40);
                this.bg.setPosition(-halfContentWidth + this.CONTENTGROWTHX_OFFSET, -this.bgTip.displayHeight - this.contentSize.height * 0.5);
            }
            else if (this.contentGrowthX === 1) {
                const halfContentWidth = Math.max(this.contentSize.width * 0.5, 40);
                this.bg.setPosition(halfContentWidth - this.CONTENTGROWTHX_OFFSET, -this.bgTip.displayHeight - this.contentSize.height * 0.5);
            }
            else { // 0
                this.bg.setPosition(0, -this.bgTip.displayHeight - this.contentSize.height * 0.5);
            }
        }
        else if (this.facing.y === 1) {
            this.bgTip.angle = 270;
            this.bgTip.setPosition(0, this.bgTip.displayHeight * 0.5);
            if (this.contentGrowthX == -1) {
                const halfContentWidth = Math.max(this.contentSize.width * 0.5, 40);
                this.bg.setPosition(-halfContentWidth + this.CONTENTGROWTHX_OFFSET, this.bgTip.displayHeight + this.contentSize.height * 0.5);
            }
            else if (this.contentGrowthX == 1) {
                const halfContentWidth = Math.max(this.contentSize.width * 0.5, 40);
                this.bg.setPosition(halfContentWidth - this.CONTENTGROWTHX_OFFSET, this.bgTip.displayHeight + this.contentSize.height * 0.5);
            }
            else { // 0
                this.bg.setPosition(0, this.bgTip.displayHeight + this.contentSize.height * 0.5);
            }
        }
        
        // reposition inner content
        // txtLabel
        this.txtLabel.setPosition(
            this.bg.x - this.contentSize.width * 0.5 + this.padding.left + this.txtLabel.displayWidth * 0.5, 
            this.bg.y,
        );
        this.icon.setPosition(
            this.bg.x + this.contentSize.width * 0.5 - this.padding.right - this.icon.displayWidth * 0.5, 
            this.bg.y,
        );
    }
}